// @core/stores/resource/ResourceStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { resourceApi } from '@core/api';
import type {
  CreateExternalResourceRequest,
  ExternalResourceResponse,
  LinkResourcesRequest,
  LinkStatsResponse,
  ListExternalResourcesResponse,
  ListLinksResponse,
  ListResourceTypesResponse,
  RefreshExternalResourceRequest,
  ResolveURNsRequest,
  ResolveURNsResponse,
  Resource,
  ResourceLinkResponse,
  ResourceTypeResponse,
  SearchResourcesResponse,
  UpdateResourceLinkRequest,
} from '@core/types/resource';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ResourceStore {
  // List state
  items: Resource[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Resource | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Resource>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Resource | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Resource | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Register external URL as resource
   */
  async CreateExternalResource(data: CreateExternalResourceRequest): Promise<ExternalResourceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.CreateExternalResource(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get external resource by URL
   */
  async GetExternalResourceByURL(params?: { url?: string | number | boolean; }): Promise<ExternalResourceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.GetExternalResourceByURL(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get link statistics for a resource
   */
  async GetLinkStats(resource_type: string, resource_id: string): Promise<LinkStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.GetLinkStats(resource_type, resource_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get resource type by name
   */
  async GetResourceType(type_name: string): Promise<ResourceTypeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.GetResourceType(type_name);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Link two resources
   */
  async LinkResources(data: LinkResourcesRequest): Promise<ResourceLinkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.LinkResources(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List external resources
   */
  async ListExternalResources(params?: PaginatedRequest): Promise<ListExternalResourcesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.ListExternalResources(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List links for a resource
   */
  async ListLinks(resource_type: string, resource_id: string, params?: PaginatedRequest): Promise<ListLinksResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.ListLinks(resource_type, resource_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List recently linked resources
   */
  async ListRecentResources(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await resourceApi.ListRecentResources(params);

      runInAction(() => {
        this.items = response.resources?.items ?? [];
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List resource types
   */
  async ListResourceTypes(params?: PaginatedRequest): Promise<ListResourceTypesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.ListResourceTypes(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Refresh external resource metadata
   */
  async RefreshExternalResource(resource_id: string, data: RefreshExternalResourceRequest): Promise<ExternalResourceResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.RefreshExternalResource(resource_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve URNs to resource previews
   */
  async ResolveURNs(data: ResolveURNsRequest): Promise<ResolveURNsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.ResolveURNs(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search resources
   */
  async SearchResources(params?: { cursor?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; q?: string | number | boolean; sort?: string | number | boolean; }): Promise<SearchResourcesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.SearchResources(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unlink two resources
   */
  async UnlinkResources(): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.UnlinkResources();

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update a resource link
   */
  async UpdateResourceLink(link_id: string, data: UpdateResourceLinkRequest): Promise<ResourceLinkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await resourceApi.UpdateResourceLink(link_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const resourceStore = new ResourceStore();
